import React, { useState, useEffect, useRef } from 'react';
import { getNote, saveNote } from '../api/client';

type Status = 'idle' | 'saving' | 'saved' | 'error';

/**
 * An auto-saving textarea bound to a server-side note keyed by string.
 * Debounced save on change; shows Saving… / Saved • time.
 */
export default function NoteBox({
  noteKey,
  title,
  accent,
  placeholder,
  minHeight = 140,
}: {
  noteKey: string;
  title: string;
  accent?: string;
  placeholder?: string;
  minHeight?: number;
}) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<Status>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const timer = useRef<number | null>(null);
  const dirty = useRef(false);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    getNote(noteKey)
      .then((n) => {
        if (!cancelled) setContent(n?.content || '');
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [noteKey]);

  const scheduleSave = (next: string) => {
    dirty.current = true;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      setStatus('saving');
      try {
        await saveNote(noteKey, next);
        setStatus('saved');
        setSavedAt(new Date());
        dirty.current = false;
      } catch {
        setStatus('error');
      }
    }, 600);
  };

  const statusLabel = (() => {
    if (loading) return 'Loading…';
    if (status === 'saving') return 'Saving…';
    if (status === 'error') return 'Save failed';
    if (status === 'saved' && savedAt) {
      return `Saved · ${savedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    }
    return '';
  })();

  return (
    <div
      className="card"
      style={{
        padding: 18,
        borderLeft: accent ? `4px solid ${accent}` : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <h3
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#0F172A',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {title}
        </h3>
        <span style={{ fontSize: 12, color: status === 'error' ? '#DC2626' : '#94A3B8' }}>
          {statusLabel}
        </span>
      </div>
      <textarea
        value={content}
        onChange={(e) => {
          setContent(e.target.value);
          scheduleSave(e.target.value);
        }}
        placeholder={placeholder}
        disabled={loading}
        style={{
          width: '100%',
          minHeight,
          resize: 'vertical',
          fontSize: 14,
          lineHeight: 1.5,
          fontFamily: 'inherit',
        }}
      />
    </div>
  );
}
